import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Screen, TopBar, Loader } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { labApi } from "@/data/api";
import { Field, TextField, Toggle, SaveBar } from "@/components/adminui";

const EMPTY = { number: "", title: "", subtitle: "", blocks: [], published: true };
const SAMPLE = `[
  { "type": "text", "body": "Intro paragraph.\\n\\nSecond paragraph." },
  { "type": "tip", "body": "A helpful tip." },
  { "type": "list", "title": "Steps", "items": ["First", "Second"] },
  { "type": "labtask", "title": "Try it", "prompt": "Do this", "fields": [{ "key": "answer", "label": "Your answer" }] }
]`;

export default function AdminLabEdit() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { token } = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [form, setForm] = useState(EMPTY);
  const [blocksText, setBlocksText] = useState("[]");
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    labApi.adminGet(id, token).then((d) => { setForm({ ...EMPTY, ...d }); setBlocksText(JSON.stringify(d.blocks || [], null, 2)); }).catch(() => toast.show("Couldn't load")).finally(() => setLoading(false));
  }, [id, token]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.show("Title is required"); return; }
    let blocks;
    try { blocks = JSON.parse(blocksText || "[]"); if (!Array.isArray(blocks)) throw new Error(); }
    catch { toast.show("Blocks must be valid JSON array"); return; }
    setSaving(true);
    const body = { number: form.number, title: form.title, subtitle: form.subtitle, blocks, published: form.published };
    try {
      if (id) await labApi.adminUpdate(id, body, token);
      else await labApi.adminCreate(body, token);
      toast.show("Saved ✨"); navigate("/admin/lab");
    } catch (e) { toast.show(e.message || "Couldn't save"); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!id || !window.confirm("Delete this experiment?")) return;
    try { await labApi.adminDelete(id, token); toast.show("Deleted"); navigate("/admin/lab"); } catch (e) { toast.show(e.message || "Couldn't delete"); }
  };

  if (loading) return <Screen><TopBar title="Edit experiment" /><Loader /></Screen>;

  return (
    <Screen>
      <TopBar title={id ? "Edit experiment" : "New experiment"} />
      <div className="ritzy-fade" style={{ padding: "16px 20px 20px" }}>
        <Field label="Number / label"><TextField value={form.number} onChange={set("number")} placeholder="e.g. 01" data-testid="lab-number" /></Field>
        <Field label="Title"><TextField value={form.title} onChange={set("title")} data-testid="lab-title" /></Field>
        <Field label="Subtitle"><TextField multiline rows={2} value={form.subtitle} onChange={set("subtitle")} /></Field>
        <Field label="Blocks (JSON)" hint="Supported types: text, tip, list, examples, compare, labtask">
          <textarea data-testid="lab-blocks" value={blocksText} onChange={(e) => setBlocksText(e.target.value)} rows={14} style={{ width: "100%", background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12, fontSize: 13, color: colors.onSurface, fontFamily: "monospace", outline: "none", boxSizing: "border-box", resize: "vertical" }} />
          <button type="button" onClick={() => setBlocksText(SAMPLE)} style={{ marginTop: 8, background: "transparent", border: `1px solid ${colors.border}`, borderRadius: 999, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: colors.brand, fontFamily: fonts.text }}>Insert sample</button>
        </Field>
        <Toggle testId="lab-published" label="Published" checked={form.published} onChange={set("published")} />
        <SaveBar onSave={save} saving={saving} onDelete={id ? del : undefined} />
      </div>
    </Screen>
  );
}
